import { type ReactElement } from "react"
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ListIcon,
  RefreshCwIcon,
  XIcon,
} from "lucide-react"
import { Link } from "react-router"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/shadcn/alert"
import { Button } from "@/components/ui/shadcn/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/shadcn/dialog"
import { Skeleton } from "@/components/ui/shadcn/skeleton"
import { cn } from "@/lib/utils"
import type { Category } from "@/types/product"

import {
  getCategoryHref,
  hasCategoryChildren,
  normalizeCategoryTree,
  splitRootCategories,
  type CatalogDisplayNode,
} from "./catalogMenuModel"
import { useCatalogMenuState } from "./useCatalogMenuState"

type CatalogMenuMobileProps = {
  categories: Category[]
  trigger?: ReactElement
}

const ROOT_GROUPS = [
  { key: "mainRoots", title: "Основные разделы" },
  { key: "miscRoots", title: "Прочее" },
] as const

export const CatalogMenuMobile = ({ categories, trigger }: CatalogMenuMobileProps) => {
  const catalogState = useCatalogMenuState(categories, {
    initialActiveCategory: "none",
    resetActiveCategoryOnClose: true,
  })
  const selectedRootCategory = catalogState.activeCategory

  return (
    <Dialog open={catalogState.open} onOpenChange={catalogState.handleOpenChange}>
      <DialogTrigger
        render={
          trigger ?? (
            <Button size="lg">
              <ListIcon data-icon="inline-start" />
              Каталог
            </Button>
          )
        }
      />
      <DialogContent
        showCloseButton={false}
        className="top-2 right-2 bottom-2 left-2 isolate flex h-auto max-h-none w-auto max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-[var(--scaena-radius-surface)] border border-white/95 bg-[linear-gradient(145deg,rgba(255,255,255,0.94)_0%,rgba(255,255,255,0.78)_48%,rgba(242,244,248,0.72)_100%),radial-gradient(circle_at_18%_0%,rgba(56,49,129,0.15),transparent_34%),radial-gradient(circle_at_96%_16%,rgba(59,130,246,0.12),transparent_28%)] p-0 font-ui shadow-none [box-shadow:0_34px_78px_-28px_rgba(17,24,39,0.5),0_16px_42px_-28px_rgba(56,49,129,0.5),inset_0_1px_0_rgba(255,255,255,0.98)] backdrop-blur-[48px] sm:max-w-none"
        data-scaena-ui
      >
        <MobileCatalogHeader
          selectedRootCategory={selectedRootCategory}
          onBack={() => catalogState.setActiveCategory(null)}
        />

        <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-3 pt-2.5 scrollbar-hidden">
          {catalogState.isLoading ? (
            <MobileCatalogSkeleton />
          ) : catalogState.isError ? (
            <MobileCatalogState
              title="Ошибка загрузки"
              description="Не удалось загрузить категории. Попробуйте снова."
              action="Попробовать снова"
              onAction={catalogState.loadCategories}
              destructive
            />
          ) : categories.length === 0 ? (
            <MobileCatalogState
              title="Каталог пока недоступен"
              description="Категории не пришли из текущего API."
              action="Обновить"
              onAction={catalogState.loadCategories}
            />
          ) : selectedRootCategory ? (
            <SelectedRootScreen
              category={selectedRootCategory}
              onNavigate={catalogState.closeMenu}
            />
          ) : (
            <RootCatalogScreen
              categories={categories}
              onSelectRoot={catalogState.setActiveCategory}
              onNavigate={catalogState.closeMenu}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

const MobileCatalogHeader = ({
  selectedRootCategory,
  onBack,
}: {
  selectedRootCategory: Category | null
  onBack: () => void
}) => (
  <header className="relative flex min-h-[56px] items-center gap-2 overflow-hidden border-b border-white/70 bg-white/44 px-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.96)] backdrop-blur-[36px]">
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-4 bottom-0 h-px bg-[linear-gradient(90deg,transparent,rgba(56,49,129,0.24),transparent)]"
    />
    {selectedRootCategory ? (
      <Button
        type="button"
        variant="floating"
        size="icon-sm"
        className="rounded-[var(--scaena-radius-control)]"
        aria-label="Назад к корневому каталогу"
        onClick={onBack}
      >
        <ArrowLeftIcon />
      </Button>
    ) : null}
    <DialogTitle className="min-w-0 flex-1 text-lg font-[650] leading-tight text-foreground">
      <span className="line-clamp-2 break-words">
        {selectedRootCategory?.name ?? "Каталог"}
      </span>
    </DialogTitle>
    <DialogClose
      render={
        <Button
          type="button"
          variant="floating"
          size="icon-sm"
          className="rounded-[var(--scaena-radius-control)]"
          aria-label="Закрыть каталог"
        />
      }
    >
      <XIcon />
    </DialogClose>
  </header>
)

const RootCatalogScreen = ({
  categories,
  onSelectRoot,
  onNavigate,
}: {
  categories: Category[]
  onSelectRoot: (category: Category) => void
  onNavigate: () => void
}) => {
  const groups = splitRootCategories(categories)

  return (
    <div className="flex flex-col gap-4">
      {ROOT_GROUPS.map(({ key, title }) => {
        const items = groups[key]

        if (items.length === 0) {
          return null
        }

        return (
          <section key={key} className="flex flex-col gap-1.5">
            <h3 className="px-2 text-xs font-[550] uppercase tracking-[0.08em] text-muted-foreground">
              {title}
            </h3>
            <div className="overflow-hidden rounded-[var(--scaena-radius-inner)] border border-white/76 bg-white/42 shadow-[var(--scaena-shadow-glass)] backdrop-blur-[32px]">
              {items.map((category, index) =>
                hasCategoryChildren(category) ? (
                  <Button
                    key={category.ref_key}
                    type="button"
                    variant="ghost"
                    className={cn(
                      "group h-auto min-h-[43px] w-full justify-start whitespace-normal rounded-none px-3.5 py-2 text-left text-sm leading-snug text-foreground/86 hover:bg-white/58",
                      index < items.length - 1 && "border-b border-white/58"
                    )}
                    onClick={() => onSelectRoot(category)}
                  >
                    <span className="min-w-0 flex-1 break-words font-[550]">
                      {category.name}
                    </span>
                    <ChevronRightIcon data-icon="inline-end" />
                  </Button>
                ) : (
                  <Button
                    key={category.ref_key}
                    render={<Link to={getCategoryHref(category)} onClick={onNavigate} />}
                    variant="ghost"
                    className={cn(
                      "h-auto min-h-[42px] w-full justify-start whitespace-normal rounded-none px-3.5 py-2 text-left text-sm font-[400] leading-snug text-foreground/82 hover:bg-white/58",
                      index < items.length - 1 && "border-b border-white/58"
                    )}
                  >
                    <span className="min-w-0 break-words">{category.name}</span>
                  </Button>
                )
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}

const SelectedRootScreen = ({
  category,
  onNavigate,
}: {
  category: Category
  onNavigate: () => void
}) => {
  const node = normalizeCategoryTree(category)

  return (
    <div className="flex flex-col gap-3">
      <Button
        render={<Link to={node.href} onClick={onNavigate} />}
        size="lg"
        className="h-auto min-h-[50px] w-full justify-between whitespace-normal rounded-[var(--scaena-radius-inner)] px-3.5 py-2.5 text-left"
      >
        <span className="min-w-0 break-words">Все товары раздела</span>
        <ArrowRightIcon data-icon="inline-end" />
      </Button>

      {node.children.length > 0 ? (
        <div className="overflow-hidden rounded-[var(--scaena-radius-inner)] border border-white/76 bg-white/42 shadow-[var(--scaena-shadow-glass)] backdrop-blur-[32px]">
          {node.children.map((child) => (
            <MobileCategoryNode key={child.category.ref_key} node={child} onNavigate={onNavigate} />
          ))}
        </div>
      ) : (
        <p className="rounded-[var(--scaena-radius-inner)] border border-white/70 bg-white/52 px-3.5 py-3 text-sm leading-6 text-muted-foreground shadow-[var(--scaena-shadow-glass)] backdrop-blur-[32px]">
          В этом разделе пока нет вложенных категорий.
        </p>
      )}
    </div>
  )
}

const MobileCategoryNode = ({
  node,
  onNavigate,
  depth = 0,
}: {
  node: CatalogDisplayNode
  onNavigate: () => void
  depth?: number
}) => {
  const hasChildren = node.children.length > 0

  return (
    <div
      className={cn(
        "flex min-w-0 flex-col",
        depth === 0 && "border-b border-white/58 last:border-b-0",
        depth > 0 && "gap-0"
      )}
    >
      <Link
        to={node.href}
        className={cn(
          "flex items-center px-3 text-sm leading-snug outline-none transition-colors duration-200 hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/15",
          depth === 0 && "min-h-[46px] rounded-[var(--scaena-radius-compact)] text-foreground hover:bg-white/58",
          depth > 0 && "min-h-[34px] rounded-[var(--scaena-radius-compact)] text-muted-foreground hover:bg-primary/8",
          hasChildren && "font-[550]"
        )}
        onClick={onNavigate}
      >
        <span className="min-w-0 break-words">{node.category.name}</span>
      </Link>
      {hasChildren ? (
        <div className="mb-2 ml-3 flex flex-col border-l border-primary/12 pl-2.5">
          {node.children.map((child) => (
            <MobileCategoryNode
              key={child.category.ref_key}
              node={child}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

const MobileCatalogSkeleton = () => (
  <div className="flex flex-col gap-5">
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-36" />
      {Array.from({ length: 7 }).map((_, index) => (
        <Skeleton key={index} className="h-12 w-full rounded-[var(--scaena-radius-inner)]" />
      ))}
    </div>
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-4 w-20" />
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-11 w-full rounded-[var(--scaena-radius-inner)]" />
      ))}
    </div>
  </div>
)

const MobileCatalogState = ({
  title,
  description,
  action,
  onAction,
  destructive = false,
}: {
  title: string
  description: string
  action: string
  onAction: () => void
  destructive?: boolean
}) => (
  <div className="flex min-h-[360px] flex-col justify-center gap-4">
    <Alert variant={destructive ? "destructive" : "default"}>
      <AlertCircleIcon />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
    <Button type="button" variant="outline" onClick={onAction}>
      <RefreshCwIcon data-icon="inline-start" />
      {action}
    </Button>
  </div>
)
